import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircle, X, Mail, Instagram, Phone, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { SOCIAL_LINKS } from '../config/social';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Facebook Messenger uses a distinct chat-bubble icon; we render it as an
// inline SVG so we don't add another icon package just for one glyph.
const MessengerIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.9 1.19 5.44 3.14 7.17V22l2.87-1.57c.77.21 1.58.32 2.42.34l.57.02c5.64 0 10-4.13 10-9.7 0-5.57-4.36-9.69-10-9.69zm.1 12.98l-2.55-2.72-4.97 2.72L10.05 9l2.6 2.73 4.92-2.73-5.47 5.98z" />
  </svg>
);

/**
 * ChatWidget — floating bottom-right bubble that expands into a small
 * panel with the coach's Messenger, e-mail and Instagram DM contact
 * options. Not a real chat: the goal is to keep the visitor one tap
 * away from a channel where the coach can actually respond.
 *
 * Mounted globally (App.js), remembers open/closed state per session.
 */
const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  // Direct-message form state — lets the visitor send a message without
  // leaving the site or opening Messenger.
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const canSend = form.name.trim() && form.email.trim() && form.message.trim() && !sending;
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!canSend) return;
    setSending(true);
    try {
      await axios.post(`${API_BASE}/api/contacts/`, {
        name:    form.name.trim(),
        email:   form.email.trim(),
        phone:   form.phone.trim(),
        message: form.message.trim(),
        source:  'chat_widget',
      });
      setSent(true);
      toast.success('Üzeneted elküldve! Hamarosan válaszolok.');
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error('Küldés sikertelen — próbáld újra, vagy írj Messengeren.');
    } finally { setSending(false); }
  };
  // Reset success screen when the widget re-opens after a while.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => { if (sent) setSent(false); }, 12000);
    return () => clearTimeout(t);
  }, [open, sent]);

  // Also listen for the custom event fired by other components (e.g. the
  // BookingModal's "Kérdésed van?" link) to programmatically open the chat.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('oc:open-chat', onOpen);
    return () => window.removeEventListener('oc:open-chat', onOpen);
  }, []);

  // Auto-open once per session after the visitor has stayed on the page for
  // ~3s with nothing else on screen. Any modal (promo popup, booking, cookie
  // banner) pauses the countdown so the two never collide.
  const [autoDone, setAutoDone] = useState(() => {
    try { return sessionStorage.getItem('oc_chat_autoopen_v1') === '1'; } catch (_e) { return true; }
  });
  const [teaser, setTeaser] = useState(false);
  useEffect(() => {
    if (autoDone || open) return undefined;
    const p = window.location.pathname || '';
    if (p.startsWith('/admin') || p.includes('/print')) return undefined;

    let quietMs = 0;
    const STEP = 250;
    const tick = setInterval(() => {
      const blocked = !!document.querySelector('[data-testid="promo-popup-card"], [role="dialog"], [data-testid="cookie-consent-banner"]');
      quietMs = blocked ? 0 : quietMs + STEP;
      if (quietMs >= 3000) {
        clearInterval(tick);
        setAutoDone(true);
        try { sessionStorage.setItem('oc_chat_autoopen_v1', '1'); } catch (_e) { /* ignore */ }
        // Mobilon csak egy kicsi, nem takaró buborék nyílik ki, hogy a hero
        // és a fő CTA látható maradjon; asztalin a teljes panel.
        if (window.innerWidth < 640) setTeaser(true);
        else setOpen(true);
      }
    }, STEP);
    return () => clearInterval(tick);
  }, [autoDone, open]);

  // Manual interaction also consumes the one-shot auto-open.
  useEffect(() => {
    if (!open || autoDone) return;
    setAutoDone(true);
    try { sessionStorage.setItem('oc_chat_autoopen_v1', '1'); } catch (_e) { /* ignore */ }
  }, [open, autoDone]);

  // Hide on print + admin pages — those aren't visitor-facing.
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const check = () => {
      const p = window.location.pathname || '';
      setVisible(!p.startsWith('/admin') && !p.includes('/print'));
    };
    check();
    window.addEventListener('popstate', check);
    return () => window.removeEventListener('popstate', check);
  }, []);
  // React Router doesn't fire popstate on programmatic navigation — piggy-back
  // on hashchange + a MutationObserver on <title> as a low-cost proxy so the
  // widget hides/appears when the user routes into /admin from the SPA.
  useEffect(() => {
    const check = () => {
      const p = window.location.pathname || '';
      setVisible(!p.startsWith('/admin') && !p.includes('/print'));
    };
    const t = setInterval(check, 800);
    return () => clearInterval(t);
  }, []);

  if (!visible) return null;

  const email = SOCIAL_LINKS.email || 'info@oroszcoaching.hu';
  const emailHref = `mailto:${email}?subject=${encodeURIComponent('Érdeklődés — Orosz Coaching')}`;

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="chat-widget-open"
          aria-label="Chat megnyitása"
          className="fixed bottom-5 right-5 z-[90] group inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-slate-900 font-bold text-sm px-4 py-3 rounded-full shadow-2xl shadow-yellow-500/30 transition-all hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400/50 focus-visible:ring-offset-2"
        >
          <MessageCircle size={20} strokeWidth={2.5} />
          <span className="hidden sm:inline">Kérdezz bátran</span>
        </button>
      )}

      {/* Mobil teaser — kis buborék, ami nem takarja a tartalmat */}
      {teaser && !open && (
        <div
          data-testid="chat-widget-teaser"
          className="fixed bottom-40 right-3 sm:bottom-20 sm:right-4 z-[89] w-[min(210px,calc(100vw-24px))] sm:w-[min(252px,calc(100vw-32px))] rounded-2xl bg-slate-900 text-white shadow-2xl border border-slate-700 p-2.5 pr-8 sm:p-3 sm:pr-9 animate-in fade-in slide-in-from-bottom-2 duration-300"
        >
          <button
            type="button"
            onClick={() => setTeaser(false)}
            data-testid="chat-widget-teaser-close"
            aria-label="Buborék bezárása"
            className="absolute top-2 right-2 w-6 h-6 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X size={14} />
          </button>
          <button
            type="button"
            onClick={() => { setTeaser(false); setOpen(true); }}
            data-testid="chat-widget-teaser-open"
            className="text-left"
          >
            <span className="block text-[13px] sm:text-sm font-bold leading-snug">Van kérdésed?</span>
            <span className="hidden sm:block text-xs text-slate-300 leading-relaxed mt-0.5">
              Írj bátran — 24 órán belül válaszolok.
            </span>
            <span className="block text-[11px] sm:text-xs font-semibold text-yellow-400 mt-1">Írok egy üzenetet →</span>
          </button>
        </div>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-widget-panel"
          role="dialog"
          aria-labelledby="chat-widget-title"
          className="fixed bottom-5 right-5 z-[90] w-[calc(100vw-40px)] max-w-[360px] max-h-[calc(100vh-40px)] overflow-y-auto rounded-2xl bg-white shadow-2xl border border-gray-200 animate-in fade-in slide-in-from-bottom-4 duration-200"
          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-4 pr-12 relative">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-500/20 border border-yellow-500/40 flex items-center justify-center shrink-0">
                <MessageCircle className="text-yellow-400" size={18} />
              </div>
              <div className="min-w-0">
                <div id="chat-widget-title" className="font-bold text-sm leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Van kérdésed?
                </div>
                <div className="text-[11px] text-gray-300 mt-0.5">
                  Válaszolok 24 órán belül munkanapokon.
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="chat-widget-close"
              aria-label="Bezárás"
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X size={14} />
            </button>
          </div>

          {/* Options */}
          <div className="p-4 space-y-2.5">
            <p className="text-[13px] text-gray-600 leading-relaxed mb-3">
              Írj a legkényelmesebb csatornán — bármit szívesen megbeszélek: programválasztás, célok, időpont.
            </p>

            {SOCIAL_LINKS.messenger && (
              <a
                href={SOCIAL_LINKS.messenger}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="chat-widget-messenger"
                className="group flex items-center gap-3 p-3 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white font-semibold text-sm hover:from-blue-600 hover:to-blue-700 shadow-lg shadow-blue-500/20 transition-all"
              >
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                  <MessengerIcon className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold">Messenger</div>
                  <div className="text-[11px] text-blue-100 truncate">Írj most Facebook Messengeren</div>
                </div>
                <span className="text-white/70 group-hover:translate-x-1 transition-transform">→</span>
              </a>
            )}

            <a
              href={emailHref}
              data-testid="chat-widget-email"
              className="group flex items-center gap-3 p-3 rounded-xl bg-slate-900 text-white font-semibold text-sm hover:bg-slate-800 shadow-lg shadow-slate-900/20 transition-all"
            >
              <div className="w-9 h-9 rounded-full bg-yellow-500/20 border border-yellow-500/40 flex items-center justify-center shrink-0">
                <Mail className="w-4 h-4 text-yellow-400" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold">E-mail</div>
                <div className="text-[11px] text-gray-300 truncate">{email}</div>
              </div>
              <span className="text-white/70 group-hover:translate-x-1 transition-transform">→</span>
            </a>

            {SOCIAL_LINKS.instagram && (
              <a
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="chat-widget-instagram"
                className="group flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-pink-400 hover:bg-pink-50/50 font-semibold text-sm text-gray-800 transition-all"
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center shrink-0">
                  <Instagram className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold">Instagram DM</div>
                  <div className="text-[11px] text-gray-500 truncate">@oroszcoaching</div>
                </div>
                <span className="text-gray-400 group-hover:translate-x-1 transition-transform">→</span>
              </a>
            )}
          </div>

          {/* Direct message form — lets the visitor send an inquiry without
              leaving the site. Posts to /api/contacts, which stores it and
              e-mails the coach. */}
          <div className="border-t border-gray-100 px-4 pt-3.5 pb-3 bg-gradient-to-b from-gray-50 to-white">
            <div className="text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Send size={11} className="text-yellow-600" /> Írj rögtön egy üzenetet
            </div>
            {sent ? (
              <div
                data-testid="chat-widget-form-success"
                className="flex items-start gap-2.5 p-3 rounded-lg bg-emerald-50 border border-emerald-200"
              >
                <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                <div className="text-[12px] text-emerald-800 leading-relaxed">
                  <strong>Megérkezett!</strong> 24 órán belül munkanapokon válaszolok az általad megadott e-mailre.
                </div>
              </div>
            ) : (
              <form onSubmit={sendMessage} className="space-y-2" data-testid="chat-widget-form">
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    data-testid="chat-widget-field-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Neved *"
                    className="text-[13px] h-9"
                    required
                  />
                  <Input
                    data-testid="chat-widget-field-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="Telefon (opcionális)"
                    className="text-[13px] h-9"
                    type="tel"
                  />
                </div>
                <Input
                  data-testid="chat-widget-field-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="E-mail *"
                  className="text-[13px] h-9"
                  type="email"
                  required
                />
                <Textarea
                  data-testid="chat-widget-field-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="A kérdésed / üzeneted *"
                  rows={3}
                  className="text-[13px] min-h-[70px] resize-none"
                  required
                />
                <button
                  type="submit"
                  disabled={!canSend}
                  data-testid="chat-widget-form-submit"
                  className="w-full inline-flex items-center justify-center gap-1.5 bg-yellow-500 hover:bg-yellow-400 disabled:bg-gray-300 disabled:cursor-not-allowed text-slate-900 font-bold text-[13px] px-3 py-2.5 rounded-lg transition-all"
                >
                  {sending ? <><Loader2 size={13} className="animate-spin" /> Küldés…</> : <><Send size={13} /> Elküldöm</>}
                </button>
                <p className="text-[10px] text-gray-500 leading-snug pt-0.5">
                  Az adataidat kizárólag a kapcsolatfelvételhez használom.
                </p>
              </form>
            )}
          </div>

          {/* Footer — direct phone line for urgent asks */}
          <div className="px-4 pb-3 pt-2 border-t border-gray-100">
            <a
              href="tel:+36702378299"
              data-testid="chat-widget-phone"
              className="flex items-center justify-center gap-2 text-xs text-gray-500 hover:text-slate-900 transition-colors"
            >
              <Phone size={12} />
              Sürgős? Hívj: +36 70 237 8299
            </a>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
